import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShoppingCart, LogOut, User, Filter, Calendar, Search } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';
import SeasonNavigation from '../components/SeasonNavigation';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

const ProgrammingCatalog = ({ user, onLogout, cart }) => {
  const { season } = useParams(); // Get season from URL
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentSeason, setCurrentSeason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, [season]); // Refetch when season changes

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // If season from URL, use it; otherwise get first available season
      let seasonParam = season ? season.toUpperCase() : '';
      
      if (!seasonParam) {
        // Get first available season
        const seasonsResponse = await axios.get(`${API}/programming/seasons`);
        const availableSeasons = seasonsResponse.data.seasons || [];
        if (availableSeasons.length > 0) {
          seasonParam = availableSeasons[0].season;
        } else {
          setProducts([]);
          setFilteredProducts([]);
          setLoading(false);
          return;
        }
      }
      
      setCurrentSeason(seasonParam);
      
      const response = await axios.get(`${API}/programming/products?season=${seasonParam}`, { headers });
      console.log(`Programming products loaded for ${seasonParam}:`, response.data.length);
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error loading programming products:', error);
      toast.error('Error al cargar productos de programación');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter - by model name or reference
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p => {
        const modelName = (p.model_name || p.display_name || '').toLowerCase();
        const reference = (p.sizes && p.sizes[0] && p.sizes[0].reference) ? p.sizes[0].reference.toLowerCase() : '';
        return modelName.includes(searchLower) || reference.includes(searchLower);
      });
    }

    if (selectedGender !== 'all') {
      filtered = filtered.filter(p => {
        const genero = (p.genero || '').toLowerCase().trim();
        if (selectedGender === 'hombre') {
          return genero.includes('hombre') || genero.includes('men');
        } else if (selectedGender === 'mujer') {
          return genero.includes('mujer') || genero.includes('women');
        } else if (selectedGender === 'junior') {
          return genero.includes('junior') || genero.includes('niño') || genero.includes('kid');
        }
        return false;
      });
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => {
        const familia = (p.familia || '').toLowerCase();
        return familia.includes(selectedCategory.toLowerCase()) || 
               (p.subfamilia || '').toLowerCase().includes(selectedCategory.toLowerCase());
      });
    }

    setFilteredProducts(filtered);
  };

  useEffect(() => {
    if (products.length > 0) {
      applyFilters();
    }
  }, [selectedGender, selectedCategory, products, searchTerm]);

  const getCartItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const formatServiceDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center space-x-3">
                <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10" />
                <span className="text-white text-xl font-bold">B2B</span>
              </Link>
              
              {/* Main Catalog Buttons */}
              <div className="flex items-center ml-8">
                <SeasonNavigation />
              </div>
            </div>

            <div className="flex items-center space-x-6">
              {user && (
                <>
                  <Link to="/cart" className="text-white hover:text-gray-300 relative">
                    <ShoppingCart className="w-6 h-6" />
                    {getCartItemCount() > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {getCartItemCount()}
                      </span>
                    )}
                  </Link>

                  {(user.role === 'admin' || user.role === 'manager') && (
                    <Link
                      to="/admin"
                      className="text-white hover:text-gray-300"
                    >
                      <User className="w-6 h-6" />
                    </Link>
                  )}

                  <Button
                    onClick={onLogout}
                    variant="ghost"
                    className="text-white hover:text-gray-300"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              )}

              {!user && (
                <Link to="/login">
                  <Button className="bg-white text-black hover:bg-gray-200">
                    Iniciar Sesión
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-8 mb-8 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2">Programación Temporada {currentSeason}</h1>
              <p className="text-xl mb-4">Novedades {(() => { const s=(currentSeason||'').toUpperCase(); const y=s.slice(-2); const yr=y.match(/\d{2}/)?('20'+y):''; return (s.startsWith('AW')||s.startsWith('FW')?'Otoño/Invierno ':'Primavera/Verano ')+yr; })()}</p>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-lg inline-block">
                <Calendar className="w-5 h-5" />
                <span className="font-semibold">Fecha de servicio: desde {products[0]?.service_date ? formatServiceDate(products[0].service_date) : 'consultar'}</span>
              </div>
              
              {/* Search Bar */}
              <div className="relative max-w-xl mt-6">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Buscar por modelo o referencia..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 py-3 text-base border-white/30 bg-white text-gray-900"
                />
              </div>
            </div>
            <div className="text-6xl ml-4">🌸</div>
          </div>
        </div>

        {/* User Status */}
        {user && (
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-black mb-2">
              Bienvenido, {user.business_name}
            </h2>
            <p className="text-gray-700">
              {user.role === 'admin' 
                ? 'Panel de administrador - Precios de tarifa (PT)' 
                : (user.approved 
                    ? 'Estás viendo precios de tarifa (PT)' 
                    : 'Estás viendo precios públicos (PVPR)'
                  )
              }
            </p>
          </div>
        )}

        {/* Approval Banner */}
        {user && user.role !== 'admin' && !user.approved && (
          <div className="max-w-4xl mx-auto mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-2xl">⏳</span>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-bold text-yellow-900">Cuenta Pendiente de Aprobación</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Actualmente puedes ver el catálogo de programación y precios públicos (PVPR), 
                  pero no podrás realizar pedidos hasta que tu cuenta sea aprobada.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className="mb-8 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl">
          <p className="text-amber-900">
            <strong>ℹ️ Importante:</strong> Estos productos son de programación y NO tienen stock físico actual. 
            Los pedidos se servirán a partir de la fecha indicada ({products[0]?.service_date ? formatServiceDate(products[0].service_date) : 'consultar'}).
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-gray-300"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
          </div>

          {showFilters && (
            <div className="bg-gray-50 rounded-xl p-6 max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Gender Filter */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Género</h3>
                  <div className="space-y-2">
                    {['all', 'hombre', 'mujer', 'junior'].map((gender) => (
                      <label key={gender} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={gender}
                          checked={selectedGender === gender}
                          onChange={(e) => setSelectedGender(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700 capitalize">
                          {gender === 'all' ? 'Todos' : gender}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Categoría</h3>
                  <div className="space-y-2">
                    {['all', 'barefoot', 'outdoor', 'urban', 'sandalias', 'botas'].map((category) => (
                      <label key={category} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          value={category}
                          checked={selectedCategory === category}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700 capitalize">
                          {category === 'all' ? 'Todas' : category}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <p className="text-gray-600 text-lg">Cargando productos...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 text-lg">No se encontraron productos con los filtros seleccionados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/programming/product/${encodeURIComponent(product.id)}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
              >
                <div className="relative">
                  {/* Programming Badge */}
                  <div className="absolute top-2 left-2 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold z-10">
                    {currentSeason}
                  </div>
                  
                  <FallbackImage
                    images={product.images}
                    alt={product.display_name}
                    className="w-full h-64 object-cover"
                  />
                </div>

                <div className="p-4">
                  {/* Product Info */}
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                    {product.display_name}
                  </h3>

                  {/* Product Reference */}
                  {product.sizes && product.sizes[0]?.reference && (
                    <p className="text-blue-600 text-sm mb-2 font-semibold">
                      REF: {product.sizes[0].reference}
                    </p>
                  )}

                  {/* Availability Date */}
                  <div className="mb-3 text-xs text-gray-500 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Disponible desde: {formatServiceDate(product.service_date)}
                  </div>

                  {/* Prices */}
                  {product.sizes && product.sizes.length > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        {!user ? (
                          <p className="text-sm text-gray-500 italic" data-testid="login-to-see-prices">Inicia sesión para ver precios</p>
                        ) : (user.role === 'admin' || user.approved) && product.sizes[0].pt_price ? (
                          <>
                            <p className="text-lg font-bold text-blue-600">
                              €{product.sizes[0].pt_price.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">Precio tarifa</p>
                            <p className="text-sm text-gray-400 line-through">
                              €{product.sizes[0].pvpr_price.toFixed(2)} PVPR
                            </p>
                          </>
                        ) : (
                          <>
                            {product.sizes[0].pvpr_price > 0 ? (
                              <>
                                <p className="text-lg font-bold text-black">
                                  €{product.sizes[0].pvpr_price.toFixed(2)}
                                </p>
                                <p className="text-xs text-gray-500">PVPR</p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500 italic">Consultar precio</p>
                            )}
                          </>
                        )}
                      </div>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                        Ver Detalles
                      </Button>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProgrammingCatalog;
